import { PropsWithChildren, ReactNode } from 'react';
import clsx from 'clsx';
import MuiTab, { TabProps } from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core/styles';
import { useTabContext } from '@material-ui/lab';
import { MDSTypography, getTypographyProps } from '@marqvision/mds-v2';
import MDSTag from './Tag';

const useStyles = makeStyles(() => ({
  tab: {
    minHeight: 'auto',
    minWidth: 'auto',
    padding: 8,
    marginRight: 0,
    '& .MuiTab-wrapper': {
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      flexDirection: 'column',
      justifyContent: 'center',
      textTransform: 'initial',
    },
  },
  labelWrapper: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    '& div': {
      cursor: 'pointer',
    },
  },
}));

type PreparedTag = { count?: number; isNew?: boolean };

type OwnProps = {
  badge?: ReactNode;
  tag?: PreparedTag;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
};
type Props = PropsWithChildren<OwnProps> & Omit<TabProps, 'children'>;

const getVariant = (size: 'small' | 'medium' | 'large' | 'xlarge') => {
  switch (size) {
    case 'small':
      return 13;
    case 'medium':
      return 14;
    case 'large':
      return 16;
    case 'xlarge':
      return 20;
  }
};
export const MDSTab = (props: Props) => {
  const classes = useStyles();
  const { label, badge, tag, size = 'medium', className, ...restProps } = props;
  const variant = getVariant(size);

  const context = useTabContext();
  const isSelected = context?.value === restProps.value;

  const { variant: mdsV2Variant, size: mdsV2Size } = getTypographyProps(variant);

  return (
    <MuiTab
      className={clsx(classes.tab, className)}
      label={
        <div className={classes.labelWrapper}>
          <MDSTypography
            weight="medium"
            variant={mdsV2Variant as 'body'}
            size={mdsV2Size as 'm'}
            color="inherit"
            className="mds-v1-tab-item-label"
          >
            {label}
          </MDSTypography>
          {tag?.count !== undefined && (
            <MDSTag
              variant="fill"
              color={isSelected ? 'blue' : 'bluegray'}
              label={Intl.NumberFormat('en-US').format(tag.count)}
              size="small"
            />
          )}
          {tag?.isNew && <MDSTag variant="fill" color="red" label="N" size="small" />}
          {badge}
        </div>
      }
      {...restProps}
    />
  );
};

export default MDSTab;
